import type { ReactNode } from 'react';
import type { SourceDescriptor } from '../../shared/contracts';
import { SourceBadge } from './SourceBadge';
import { LiveBadge } from './LiveBadge';
import { PaneError, PaneEmpty } from './States';

interface PaneFrameProps {
  title: string;
  source: SourceDescriptor;
  /** Pane body; omitted/ignored for `error`/`empty` modes, which render their own state. */
  children?: ReactNode;
  className?: string;
  /** Small department label above the title. */
  eyebrow?: string;
  /** The window the numbers cover — always the RESOLVED period, so it never lies. */
  periodLabel?: string;
  /** When set, the card is clickable and opens the drill-down modal. */
  onOpen?: () => void;
}

export function PaneFrame({ title, source, children, className = '', eyebrow, periodLabel, onOpen }: PaneFrameProps) {
  const clickable = Boolean(onOpen);
  return (
    <div
      className={`group relative flex h-full max-h-[28rem] flex-col rounded-brand border border-brand-primary/10 bg-white p-4 shadow-sm transition-all animate-fade-in-up ${
        clickable ? 'cursor-pointer hover:-translate-y-0.5 hover:border-brand-primary/25 hover:shadow-md' : ''
      } ${className}`}
      onClick={onOpen}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          {eyebrow && <div className="mb-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-brand-primary/45">{eyebrow}</div>}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="font-heading text-[15px] font-semibold text-brand-dark">{title}</h3>
            {periodLabel && (
              <span className="whitespace-nowrap rounded-full bg-brand-primary/5 px-2 py-0.5 text-[10px] font-medium text-brand-dark/55">
                {periodLabel}
              </span>
            )}
            <LiveBadge mode={source.mode} />
          </div>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <SourceBadge source={source} />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {source.mode === 'error' ? (
          <PaneError reason={source.errorReason} />
        ) : source.mode === 'empty' ? (
          <PaneEmpty />
        ) : (
          children
        )}
      </div>
    </div>
  );
}
